// NOTE: This is specific to Coles.
// Digitised Proof of Age (BD-8243) — restricted category detection.

import type { RestrictedCategory } from '../model/poa.types'
import type { OrderDetails } from '@/entities/order/model'
import { getOrderType, isPartnerOrderType } from '@/entities/order/lib'
import { parseBarcodeToken } from './order.crate'
import { getRestrictedItemsDisplay } from './order.restricted'

/**
 * Coles crate area codes that denote a restricted category.
 *
 * Mirrors the server-side constant in
 * `tempo_screen_api/constants/areaCodeCrateTypeMapping`, where 41 is labelled
 * "Restricted" (tobacco) and 42 "Liquor". We key off the numeric code rather than
 * that label: the label is a backend string with no client contract, and "Restricted"
 * does not name the category it represents.
 */
export const RESTRICTED_AREA_CODES: Record<string, RestrictedCategory> = {
  41: 'tobacco',
  42: 'liquor',
}

/**
 * Area codes of the crates that belong to this order, read from
 * `customEventMetaData.array_barcodes` (`A<orderId>*areaCode*crateNumber`).
 *
 * Deliberately does NOT reuse `getExpectedCrateItemsFromArrayBarcodes()`: that helper
 * resolves each barcode through `order.areaCodeCrateTypeMapping`, which the backend
 * strips from the response whenever `zebraBarcodeEnabled` is off for the store
 * (`OrderHandler.displayDdbOrder`, `eventEmitterHelpers`). Since Zebra and Proof of
 * Age are independent toggles, routing detection through the mapping would report
 * "no restricted items" for every order in a non-Zebra store — silently disabling a
 * compliance gate. `array_barcodes` itself is never stripped, and Coles sends it
 * regardless of the Zebra toggle, so the area code is read straight out of it.
 */
function getOwnAreaCodes(order?: OrderDetails): string[] {
  const barcodes = order?.customEventMetaData?.array_barcodes?.split(',') ?? []
  const areaCodes: string[] = []

  for (const token of barcodes) {
    // Same token validity rules as the crate helpers, so both agree on which
    // barcodes are eligible. Whitespace around a token is tolerated.
    const parsed = parseBarcodeToken(token.trim())
    if (!parsed) {
      continue
    }

    // Guard against barcodes belonging to another order, matching the check the
    // crate helpers apply when parsing the same field (A<orderId>, BD-8685).
    if (parsed.orderId !== `A${order?.orderId}`) {
      continue
    }

    areaCodes.push(parsed.areaCode)
  }

  return areaCodes
}

/** Restricted categories present in an order, from its crates' area codes. */
export function getRestrictedCategories(order?: OrderDetails): RestrictedCategory[] {
  const found = new Set<RestrictedCategory>()

  for (const areaCode of getOwnAreaCodes(order)) {
    const category = RESTRICTED_AREA_CODES[areaCode]
    if (category) {
      found.add(category)
    }
  }

  return [...found]
}

/**
 * Whether an order needs the Proof of Age checks before it can be completed.
 *
 * An order qualifies when it carries a restricted area code, or when it is flagged
 * restricted but no area code could be read. That second case should not occur in
 * production — Coles sends `array_barcodes` on every order — but a restricted order
 * whose categories we cannot determine must still be checked. Failing closed on a
 * compliance gate is correct; failing open is not.
 *
 * Once the crates can be read they decide, and the flag is ignored: an order whose
 * crates are all unrestricted needs no checks even if it is flagged (BD-8766).
 *
 * Callers are responsible for the feature toggle (`useDigitisedPoaEnabled`) and for
 * only gating the completion action.
 */
export function isPoaRequired(order?: OrderDetails): boolean {
  if (!order) {
    return false
  }

  // Partner Delivery is out of scope: delivery partners guarantee their drivers are
  // of legal age and run their own RSA process, so the team member performs no check
  // when handing to a driver. These orders can still carry liquor crates, so this
  // must be an explicit exclusion rather than something the category check happens
  // to miss. Grouped orders are partner orders by construction, so this also keeps
  // the checks off every group card.
  if (isPartnerOrderType(getOrderType(order.hsFields))) {
    return false
  }

  if (getRestrictedCategories(order).length > 0) {
    return true
  }

  if (getOwnAreaCodes(order).length > 0) {
    return false
  }

  return getRestrictedItemsDisplay(order.hsFields) === 'Yes'
}
